import { HStack } from '../primitives/Stack.js';
import { Pressable } from '../primitives/Pressable.js';
import { cx } from '../primitives/cx.js';
import { Chip } from './Chip.js';
import type { ChipTone } from './Chip.js';

export type ChipOption = {
  /** Stable id. What `onSelect` reports, and what `value` is compared to. */
  value: string;
  /** Already translated. */
  label: string;
};

export type ChipGroupProps = {
  options: readonly ChipOption[];
  /** The selected option, or null for none. */
  value?: string | null;
  onSelect: (value: string) => void;
  /**
   * Tapping the selected chip clears it, reporting `''`.
   *
   * Right for an optional answer ("which anchor?"), wrong for a required one
   * ("which category?") — clearing a required field leaves the form in a
   * state the student cannot see the problem with.
   */
  clearable?: boolean;
  /** Names the group for screen readers: "Subject", "Anchor". */
  label: string;
  tone?: ChipTone;
  className?: string;
};

/**
 * A row of one-tap answers.
 *
 * Chips are the primary way this app collects anything, and typing is the
 * escape hatch — because choosing is not the same work as composing. Asked to
 * write what gets in their way, most people give up or answer "laziness";
 * offered five specific options, they pick the true one.
 *
 * It existed as a copy-pasted `Pressable` wrapping a `Chip` in five places
 * with slightly different markup, which meant `aria-pressed` was set in some
 * of them and not others. One implementation, so the selected state is
 * announced everywhere and reads the same way.
 */
export function ChipGroup({
  options,
  value = null,
  onSelect,
  clearable = false,
  label,
  tone = 'accent',
  className,
}: ChipGroupProps) {
  return (
    <HStack
      role="group"
      aria-label={label}
      gap={2}
      className={cx('flex-wrap', className)}
    >
      {options.map((option) => {
        const on = value === option.value;
        return (
          <Pressable
            key={option.value}
            aria-pressed={on}
            onClick={() => onSelect(on && clearable ? '' : option.value)}
            className="rounded-full"
          >
            <Chip tone={on ? tone : 'neutral'}>{option.label}</Chip>
          </Pressable>
        );
      })}
    </HStack>
  );
}
